<template>
	<div class="help-message-actions">
		<button
			type="button"
			:data-testid="`help-assistant-copy-${index}`"
			aria-label="Copy response"
			class="help-message-actions__button"
			:class="{ 'help-message-actions__button--active': copied }"
			@click="$emit('copy')"
		>
			<LucideIcon
				:name="copied ? 'Check' : 'Copy'"
				:size="16"
			/>
		</button>

		<button
			type="button"
			:data-testid="`help-assistant-thumbs-up-${index}`"
			aria-label="Good response"
			:aria-pressed="rating === 'up'"
			:disabled="rateDisabled"
			class="help-message-actions__button"
			@click="$emit('rate', 'up')"
		>
			<LucideIcon
				name="ThumbsUp"
				:size="16"
			/>
		</button>

		<button
			type="button"
			:data-testid="`help-assistant-thumbs-down-${index}`"
			aria-label="Bad response"
			:aria-pressed="rating === 'down'"
			:disabled="rateDisabled"
			class="help-message-actions__button"
			@click="$emit('rate', 'down')"
		>
			<LucideIcon
				name="ThumbsDown"
				:size="16"
			/>
		</button>

		<button
			v-if="isLast"
			type="button"
			:data-testid="`help-assistant-regenerate-${index}`"
			aria-label="Regenerate response"
			class="help-message-actions__button"
			@click="$emit('regenerate')"
		>
			<LucideIcon
				name="RefreshCw"
				:size="16"
			/>
		</button>

		<span
			v-if="timestamp"
			:data-testid="`help-assistant-timestamp-${index}`"
			class="help-message-actions__timestamp"
		>
			{{ formatMessageTimestamp(timestamp) }}
		</span>
	</div>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import { formatMessageTimestamp } from './utils/chat-core.js'

/**
 * The row of controls beneath a finished assistant answer: copy, thumbs, regenerate, timestamp
 * (AB#27211). The Vue twin of the React HelpAssistant's action row.
 *
 * Extracted from `help-message-list.vue`, which had grown past the 600-line lint ceiling. It is a
 * clean seam: the row owns no state of its own, so everything it shows arrives as a prop and every
 * click leaves as an event for the panel to act on.
 *
 * Emits:
 * - `copy` (no payload - the host knows which message this row belongs to)
 * - `rate` `'up' | 'down'`
 * - `regenerate` (no payload - always the last assistant message)
 */
export default {
	name: 'help-message-actions',
	components: { LucideIcon },
	props: {
		/** Position in the transcript, used only to build stable test ids. */
		index: {
			type: Number,
			required: true
		},
		/** The caller's own rating on this message, or null when unrated. */
		rating: {
			type: String,
			default: null
		},
		/**
		 * Thumbs are locked once a rating is given, and disabled before the server has assigned a
		 * message id, since feedback has nothing to attach to until then.
		 */
		rateDisabled: {
			type: Boolean,
			default: false
		},
		/** Whether this message's text is the one currently on the clipboard. */
		copied: {
			type: Boolean,
			default: false
		},
		/** Only the last assistant message can be regenerated. */
		isLast: {
			type: Boolean,
			default: false
		},
		/** ISO 8601; rendered in the reader's own timezone. Omitted when the server sent none. */
		timestamp: {
			type: String,
			default: ''
		}
	},
	methods: {
		formatMessageTimestamp
	}
}
</script>

<style lang="scss" scoped>
.help-message-actions {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 2px;
	padding: 0 4px;

	/*
		Matches the header icon buttons on both platforms (HelpPanelTrigger, ProductSwitcher): a 6px
		radius, a half-strength muted hover and a primary focus ring, not the 4px chip radius and
		full-strength hover this row used to carry (AB#27211).
	*/
	&__button {
		display: flex;
		align-items: center;
		justify-content: center;
		min-width: 24px;
		min-height: 24px;
		padding: 6px;
		color: var(--muted-foreground, #666E7A);
		cursor: pointer;
		background: none;
		border: none;
		border-radius: calc(var(--radius-md, 0.5rem) - 2px);
		transition: background-color 0.15s ease, color 0.15s ease;

		&:hover:not(:disabled) {
			color: var(--foreground, #4A5568);
			background-color: var(--muted, #F5F5F5);
			// Half-strength, so the hover reads as a lift rather than a filled chip. Declared after
			// the solid fallback so a browser without color-mix keeps the older treatment.
			background-color: color-mix(in srgb, var(--muted, #F5F5F5) 50%, transparent);
		}

		&:focus-visible {
			outline: 2px solid var(--primary, #406A94);
			outline-offset: 0;
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}

		// An engaged control (copied, or a rating the user has given) reads in the brand colour, and
		// the thumb fills, so the row shows at a glance what has already been said about an answer.
		// The svg fill override beats LucideIcon's fill="none" presentation attribute.
		&--active,
		&[aria-pressed='true'] {
			color: var(--primary, #406A94);
		}

		&[aria-pressed='true'] ::v-deep svg {
			fill: currentColor;
		}
	}

	&__timestamp {
		padding: 0 4px;
		font-size: 0.75rem;
		color: var(--muted-foreground, #666E7A);
	}
}
</style>
